import React, { useState } from 'react';
import { useAudioCapture, AudioChunk, AudioCaptureError } from '../hooks/useAudioCapture';

export const AudioCapture: React.FC = () => {
  const [chunks, setChunks] = useState<AudioChunk[]>([]);
  const [lastError, setLastError] = useState<AudioCaptureError | null>(null);

  const handleAudioChunk = (chunk: AudioChunk) => {
    console.log('Audio chunk received:', {
      size: chunk.data.size,
      timestamp: chunk.timestamp,
      sequenceId: chunk.sequenceId,
      type: chunk.data.type
    });
    
    setChunks(prev => [...prev.slice(-9), chunk]); // Keep last 10 chunks for display
  };

  const handleError = (error: AudioCaptureError) => {
    console.error('Audio capture error:', error);
    setLastError(error);
  };

  const {
    isRecording,
    isSupported,
    error,
    startRecording,
    stopRecording
  } = useAudioCapture({
    chunkInterval: 1500, // 1.5 seconds
    onAudioChunk: handleAudioChunk,
    onError: handleError
  });

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Audio Capture Test</h2>
      
      {/* Support Status */}
      <div className="mb-4 p-3 rounded-lg bg-gray-100">
        <p className="font-semibold">
          WebM/Opus Support: 
          <span className={`ml-2 ${isSupported ? 'text-green-600' : 'text-red-600'}`}>
            {isSupported ? '✓ Supported' : '✗ Not Supported'}
          </span>
        </p>
      </div>

      {/* Error Display */}
      {(error || lastError) && (
        <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-300">
          <p className="text-red-700 font-semibold">Error:</p>
          <p className="text-red-600">{(error || lastError)?.message}</p>
          <p className="text-sm text-red-500 mt-1">Type: {(error || lastError)?.type}</p>
        </div>
      )}

      {/* Controls */}
      <div className="mb-6">
        <button
          onClick={isRecording ? stopRecording : startRecording}
          disabled={!isSupported}
          className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
            !isSupported 
              ? 'bg-gray-400 cursor-not-allowed'
              : isRecording 
                ? 'bg-red-500 hover:bg-red-600' 
                : 'bg-green-500 hover:bg-green-600'
          }`}
        >
          {isRecording ? 'Stop Recording' : 'Start Recording'}
        </button>
        
        {isRecording && (
          <div className="inline-flex items-center ml-4">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse mr-2"></div>
            <span className="text-red-600 font-medium">Recording...</span>
          </div>
        )}
      </div>

      {/* Chunk Information */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">
          Audio Chunks ({chunks.length} total)
        </h3>
        
        {chunks.length === 0 ? (
          <p className="text-gray-500 italic">No chunks captured yet</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {chunks.map((chunk) => (
              <div 
                key={`${chunk.timestamp}-${chunk.sequenceId}`}
                className="p-3 bg-gray-50 rounded border text-sm"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium">Sequence:</span> #{chunk.sequenceId}
                  </div>
                  <div>
                    <span className="font-medium">Size:</span> {formatBytes(chunk.data.size)}
                  </div>
                  <div>
                    <span className="font-medium">Time:</span> {formatTimestamp(chunk.timestamp)}
                  </div>
                  <div>
                    <span className="font-medium">Type:</span> {chunk.data.type}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Statistics */}
      {chunks.length > 0 && (
        <div className="p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2">Statistics</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Total Chunks:</span> {chunks.length}
            </div>
            <div>
              <span className="font-medium">Total Size:</span> {
                formatBytes(chunks.reduce((sum, chunk) => sum + chunk.data.size, 0))
              }
            </div>
            <div>
              <span className="font-medium">Avg Chunk Size:</span> {
                chunks.length > 0 
                  ? formatBytes(chunks.reduce((sum, chunk) => sum + chunk.data.size, 0) / chunks.length)
                  : '0 Bytes'
              }
            </div>
            <div>
              <span className="font-medium">Latest Sequence:</span> {
                chunks.length > 0 ? chunks[chunks.length - 1]?.sequenceId ?? 'N/A' : 'N/A'
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
};